import React, { useEffect, useMemo, useRef } from 'react';
import Dialog from './TivigoDialog';
import AndroidRoundedIcon from '@mui/icons-material/AndroidRounded';
import AppleIcon from '@mui/icons-material/Apple';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LaptopMacRoundedIcon from '@mui/icons-material/LaptopMacRounded';
import WindowRoundedIcon from '@mui/icons-material/WindowRounded';
import { detectUserDevicePlatform } from './userPanelDevicePlatform';
import './userPanelWindowsDownloadModal.css';

const PLATFORM_PROMOTIONS = {
  windows: {
    id: 'windows',
    name: 'Windows',
    kicker: 'TIVIGO FOR WINDOWS',
    icon: WindowRoundedIcon,
    contentStatus: 'Windows’ta hazır.',
    fallbackStatus: 'uygulamasını hemen indir.',
    actionLabel: 'Windows kurulumuna git',
    setupDetailId: 'tivigo-pc',
    actionSuffix: '→',
    deviceLabel: 'Windows 10 / 11',
    versionLabel: 'v0.1.36 · 64-bit',
    previewLabel: 'Resmî uygulama ekranı',
    mediaType: 'video',
    mediaSrc: '/assets/images/product/windows-dashboard-tour-v6.mp4',
    mediaPoster: '/assets/images/product/windows-home.jpg',
    mediaWidth: 1280,
    mediaHeight: 764,
  },
  android: {
    id: 'android',
    name: 'Android',
    kicker: 'TIVIGO FOR ANDROID',
    icon: AndroidRoundedIcon,
    contentStatus: 'Android’de hazır.',
    fallbackStatus: 'uygulamasını hemen indir.',
    actionLabel: 'Android kurulumuna git',
    setupDetailId: 'tivigo',
    actionSuffix: '→',
    deviceLabel: 'Android telefon ve TV',
    versionLabel: 'Google Play',
    previewLabel: 'Gerçek Android uygulama turu',
    mediaType: 'video',
    mediaSrc: '/assets/images/product/android-tour-20260713.mp4',
    mediaWidth: 360,
    mediaHeight: 802,
  },
  ios: {
    id: 'ios',
    name: 'iOS',
    kicker: 'TIVIGO FOR IOS',
    icon: AppleIcon,
    contentStatus: 'iOS’ta hazır.',
    fallbackStatus: 'uygulamasını hemen indir.',
    actionLabel: 'Tivigo iOS kurulumuna git',
    setupDetailId: 'smarters-lite',
    actionSuffix: '→',
    deviceLabel: 'iPhone ve iPad',
    versionLabel: 'App Store',
    previewLabel: 'Tivigo iOS uygulama turu',
    mediaType: 'video',
    mediaSrc: '/assets/images/product/android-tour-20260713.mp4',
    mediaWidth: 360,
    mediaHeight: 802,
  },
  macos: {
    id: 'macos',
    name: 'macOS',
    kicker: 'TIVIGO FOR MACOS',
    icon: LaptopMacRoundedIcon,
    contentStatus: 'macOS’te hazır.',
    fallbackStatus: 'uygulamasını hemen indir.',
    actionLabel: 'macOS kurulumuna git',
    setupDetailId: 'tivigo-macos',
    actionSuffix: '→',
    deviceLabel: 'Mac',
    versionLabel: 'DMG',
    previewLabel: 'Tivigo macOS uygulama turu',
    mediaType: 'video',
    mediaSrc: '/assets/images/product/windows-dashboard-tour-v6.mp4',
    mediaPoster: '/assets/images/product/windows-home.jpg',
    mediaWidth: 1280,
    mediaHeight: 764,
  },
};

const getContentDisplayName = (name) => String(name || '')
  .replace(/^\s*(4K|FHD|HD|SD)\s*:\s*/i, '')
  .trim();

function UserPanelWindowsDownloadModal({ open, onClose, onOpenSetup, content, contentType }) {
  const previewVideoRef = useRef(null);
  const contentName = useMemo(() => getContentDisplayName(content?.name || content?.title), [content]);
  const platform = useMemo(() => PLATFORM_PROMOTIONS[detectUserDevicePlatform()], []);
  const PlatformIcon = platform.icon;
  const contentLabel = contentType === 'movie'
    ? 'Bu filmi'
    : contentType === 'series'
      ? 'Bu diziyi'
      : contentType === 'channel'
        ? 'Bu kanalı'
        : 'İçerikleri';
  const description = platform.id === 'android'
    ? `${contentLabel} Android telefon veya TV’nizde kesintisiz izlemek ve hesabınızı yönetmek için resmî Tivigo uygulamasını indirin.`
    : platform.id === 'ios'
      ? `${contentLabel} iPhone veya iPad’inizde kesintisiz izlemek ve hesabınızı yönetmek için Tivigo iOS uygulamasını indirin.`
      : platform.id === 'macos'
        ? `${contentLabel} Mac’inizde kesintisiz izlemek ve hesabınızı yönetmek için resmî Tivigo macOS uygulamasını indirin.`
      : `${contentLabel} kesintisiz izlemek ve hesabınızı yönetmek için resmî Tivigo Windows uygulamasını indirin.`;
  const openPlatformSetup = () => {
    if (!platform.setupDetailId || !onOpenSetup) return;
    onClose?.();
    onOpenSetup(platform.setupDetailId);
  };

  useEffect(() => {
    const video = previewVideoRef.current;
    if (!video || platform.mediaType !== 'video') return undefined;

    if (!open) {
      video.pause();
      return undefined;
    }

    video.muted = true;
    video.defaultMuted = true;

    const startPlayback = () => {
      const playback = video.play();
      if (playback?.catch) playback.catch(() => {});
    };
    const animationFrame = window.requestAnimationFrame(startPlayback);
    video.addEventListener('canplay', startPlayback, { once: true });

    return () => {
      window.cancelAnimationFrame(animationFrame);
      video.removeEventListener('canplay', startPlayback);
      video.pause();
    };
  }, [open, platform.mediaSrc, platform.mediaType]);

  return (
    <Dialog
      open={open}
      maxWidth={false}
      keepMounted
      transitionDuration={0}
      disableEscapeKeyDown
      className={`tvup-windows-download-dialog is-${platform.id}`}
      aria-labelledby="tvup-windows-download-title"
      aria-describedby="tvup-windows-download-description"
      slotProps={{
        backdrop: { className: 'tvup-windows-download-backdrop' },
        paper: { id: 'tvup-windows-download-modal', className: 'tvup-windows-download-modal' },
      }}
    >
      <button
        type="button"
        className="tvup-windows-download-close"
        aria-label={`${platform.name} uygulaması penceresini kapat`}
        onClick={onClose}
      >
        <CloseRoundedIcon />
      </button>

      <div className="tvup-windows-download-body">
        <div className="tvup-windows-download-copy">
          <p className="tvup-windows-download-kicker"><span /> {platform.kicker}</p>
          <h2 id="tvup-windows-download-title">
            {contentName ? (
              <>{contentName}, <span>{platform.contentStatus}</span></>
            ) : (
              <>Tivigo {platform.name} <span>{platform.fallbackStatus}</span></>
            )}
          </h2>
          <p id="tvup-windows-download-description">
            {description}
          </p>

          <button
            className="tvup-windows-download-submit"
            type="button"
            disabled={!onOpenSetup || !platform.setupDetailId}
            onClick={openPlatformSetup}
          >
            <PlatformIcon /> {platform.actionLabel} <span aria-hidden="true">{platform.actionSuffix}</span>
          </button>
          <div className="tvup-windows-download-meta" aria-label={`${platform.name} uygulaması bilgileri`}>
            <span><i /> {platform.deviceLabel}</span>
            <span>{platform.versionLabel}</span>
          </div>
        </div>

        <div className="tvup-windows-download-visual">
          <div className={`tvup-windows-download-preview is-${platform.id}${platform.id !== 'windows' ? ' is-mobile-app' : ''}`}>
            <div className="tvup-windows-download-window-bar">
              <span className="tvup-windows-download-window-controls" aria-hidden="true"><i /><i /><i /></span>
              <b><PlatformIcon /> Tivigo for {platform.name}</b>
              <small>{platform.versionLabel}</small>
            </div>
            <div className="tvup-windows-download-window-media">
              {platform.mediaType === 'video' ? (
                <video
                  ref={previewVideoRef}
                  src={platform.mediaSrc}
                  poster={platform.mediaPoster}
                  width={platform.mediaWidth}
                  height={platform.mediaHeight}
                  aria-label={platform.previewLabel}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img
                  src={platform.mediaSrc}
                  width={platform.mediaWidth}
                  height={platform.mediaHeight}
                  alt={platform.previewLabel}
                />
              )}
              <span><i /> {platform.previewLabel}</span>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

export default UserPanelWindowsDownloadModal;
